/**
 * 프로젝트 관련 상수 정의 (클라이언트 안전)
 * type-graphql 의존성이 없어 클라이언트에서 안전하게 사용 가능
 */

// ============================================================================
// 점검 유형 (SSOT - Single Source of Truth)
// ============================================================================

/**
 * 점검 유형 배열 (DB 저장, API, DOCX 렌더링 모두 동일)
 * - checklist: 성능점검
 * - maintenance: 유지관리
 */
export const DOCX_INSPECTION_TYPES = ["checklist", "maintenance"] as const;

/**
 * 점검 유형 (DB 저장, DOCX 렌더링 통합)
 */
export type DocxInspectionType = (typeof DOCX_INSPECTION_TYPES)[number];

/**
 * 점검 유형 → UI 라벨 매핑
 */
export const DOCX_INSPECTION_TYPE_LABELS: Record<DocxInspectionType, string> = {
  checklist: "성능점검",
  maintenance: "유지관리",
};

// ============================================================================
// 프로젝트 상태
// ============================================================================

/**
 * 프로젝트 상태 배열 (SSOT)
 * - IN_PROGRESS: 진행중
 * - COMPLETED: 완료
 * - PENDING: 대기
 */
export const PROJECT_STATUSES = ["IN_PROGRESS", "COMPLETED", "PENDING"] as const;

/**
 * 프로젝트 상태 타입 (SSOT)
 */
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

// ============================================================================
// 프로젝트 유형 (클라이언트용 - GraphQL enum 키 기반)
// ============================================================================

/**
 * 프로젝트 유형 GraphQL enum 키 (클라이언트에서 사용)
 * DB 저장값(checklist/maintenance)과 다름 - GraphQL API에서 변환됨
 */
export const PROJECT_KINDS = ["PERFORMANCE_CHECK", "MAINTENANCE"] as const;

/**
 * 프로젝트 유형 타입 (GraphQL enum 키)
 */
export type ProjectKindKey = (typeof PROJECT_KINDS)[number];

/**
 * 프로젝트 유형 → UI 라벨 매핑
 */
export const PROJECT_KIND_LABELS: Record<ProjectKindKey, string> = {
  PERFORMANCE_CHECK: "성능점검",
  MAINTENANCE: "유지관리",
};

/**
 * 프로젝트 유형 옵션 (Select/Dropdown용)
 */
export const PROJECT_KIND_OPTIONS = PROJECT_KINDS.map((value) => ({
  value,
  label: PROJECT_KIND_LABELS[value],
}));

// ============================================================================
// 프로젝트 상태 라벨 및 옵션
// ============================================================================

/**
 * 프로젝트 상태 → UI 라벨 매핑
 */
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  IN_PROGRESS: "진행중",
  COMPLETED: "완료",
  PENDING: "대기",
};

/**
 * 프로젝트 상태 옵션 (Select/Dropdown용)
 */
export const PROJECT_STATUS_OPTIONS = PROJECT_STATUSES.map((value) => ({
  value,
  label: PROJECT_STATUS_LABELS[value],
}));

/**
 * 프로젝트 상태별 스타일 (Tailwind CSS)
 */
export const PROJECT_STATUS_STYLES: Record<
  ProjectStatus,
  { color: string; bgColor: string; borderColor: string }
> = {
  IN_PROGRESS: {
    color: "text-emerald-700",
    bgColor: "bg-emerald-500/15",
    borderColor: "border-emerald-200",
  },
  COMPLETED: {
    color: "text-slate-700",
    bgColor: "bg-slate-100",
    borderColor: "border-slate-200",
  },
  PENDING: {
    color: "text-amber-700",
    bgColor: "bg-amber-500/15",
    borderColor: "border-amber-200",
  },
};

// ============================================================================
// 유틸리티 함수
// ============================================================================

/**
 * 프로젝트 유형 라벨 조회
 */
export function getProjectKindLabel(kind: string): string {
  return PROJECT_KIND_LABELS[kind as ProjectKindKey] || kind;
}

/**
 * 프로젝트 상태 라벨 조회
 */
export function getProjectStatusLabel(status: string): string {
  return PROJECT_STATUS_LABELS[status as ProjectStatus] || status;
}

/**
 * 프로젝트 상태 스타일 조회
 */
export function getProjectStatusStyle(status: string) {
  return (
    PROJECT_STATUS_STYLES[status as ProjectStatus] || {
      color: "text-gray-700",
      bgColor: "bg-gray-100",
      borderColor: "border-gray-200",
    }
  );
}

// ============================================================================
// 페이지네이션 상수
// ============================================================================

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 20, 50];
